import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getHospital, createHospital, updateHospital } from '../services/hospitalService';
import type { Hospital } from '../types';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Alert } from '../components/ui/Alert';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';

export default function HospitalFormPage() {
  const navigate = useNavigate();
  const { hospitalUuid } = useParams<{ hospitalUuid: string }>();
  const isEditMode = Boolean(hospitalUuid);

  const [hospitalName, setHospitalName] = useState('');
  const [hospitalCode, setHospitalCode] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ name?: string; code?: string }>({});

  useEffect(() => {
    if (isEditMode && hospitalUuid) {
      setIsLoadingData(true);
      getHospital(hospitalUuid)
        .then((hospital: Hospital) => {
          setHospitalName(hospital.hospitalName);
          setHospitalCode(hospital.hospitalCode);
          setIsActive(hospital.isActive);
        })
        .catch(() => {
          setError('Failed to load hospital data.');
        })
        .finally(() => setIsLoadingData(false));
    }
  }, [isEditMode, hospitalUuid]);

  function validate(): boolean {
    const errors: { name?: string; code?: string } = {};
    if (!hospitalName.trim()) {
      errors.name = 'Hospital name is required';
    }
    if (!hospitalCode.trim()) {
      errors.code = 'Hospital code is required';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!validate()) return;

    setIsSubmitting(true);
    try {
      if (isEditMode && hospitalUuid) {
        await updateHospital(hospitalUuid, {
          hospitalName: hospitalName.trim(),
          hospitalCode: hospitalCode.trim(),
          isActive,
        });
        setSuccess('Hospital updated successfully.');
        setTimeout(() => navigate('/hospitals'), 1500);
      } else {
        const hospital = await createHospital(hospitalName.trim(), hospitalCode.trim());
        setSuccess(
          `Hospital created successfully. UUID: ${hospital.hospitalUuid}, ID: ${hospital.hospitalId}`
        );
        setTimeout(() => navigate('/hospitals'), 2000);
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { error?: string } } };
      const status = axiosErr.response?.status;
      const message = axiosErr.response?.data?.error;

      if (status === 409) {
        setError('Hospital code already exists. Please use a different code.');
      } else if (status === 400) {
        setError(message || 'Validation error. Please check your input.');
      } else {
        setError(message || 'An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isLoadingData) {
    return <LoadingSpinner message="Loading hospital data..." />;
  }

  return (
    <div className="hospital-form-page">
      <h2>{isEditMode ? 'Edit Hospital' : 'Create Hospital'}</h2>

      {error && (
        <Alert type="error" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert type="success" onDismiss={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <Input
          label="Hospital Name"
          value={hospitalName}
          onChange={(e) => setHospitalName(e.target.value)}
          error={fieldErrors.name}
          required
          placeholder="Enter hospital name"
        />

        <Input
          label="Hospital Code"
          value={hospitalCode}
          onChange={(e) => setHospitalCode(e.target.value)}
          error={fieldErrors.code}
          required
          placeholder="Enter unique hospital code"
        />

        {isEditMode && (
          <div className="form-field">
            <label htmlFor="is-active-checkbox">
              <input
                id="is-active-checkbox"
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />{' '}
              Active
            </label>
          </div>
        )}

        <div className="form-actions">
          <Button type="submit" isLoading={isSubmitting} disabled={isSubmitting}>
            {isEditMode ? 'Update Hospital' : 'Create Hospital'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/hospitals')}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
